import { useEffect } from "react";
import { createRoot } from "react-dom/client";
import { AlertTriangle, Wallet, X } from "lucide-react";
import { formatRupees } from "./formatRupees";

/**
 * Prominent, centered confirmation popup shown when a user reaches their FREE
 * limit and the next action will deduct credits. Promise-based (like
 * confirmToast) so it can be awaited from anywhere — resolves `true` on confirm,
 * `false` on cancel / dismiss. Rendered into its own portal root on document.body
 * so it doesn't need to live in any component's tree.
 *
 * The point is to NEVER cut credits silently: on hitting the limit the user sees
 * exactly what it costs and must confirm.
 *
 * @param {object} p
 * @param {number} p.cost - credits that will be deducted
 * @param {number} p.balance - the user's current balance
 * @param {number} [p.freeLimit] - the free allowance (for the "used all N free…" line)
 * @param {string} [p.unit] - singular noun, e.g. "private upload" / "property"
 * @param {string} [p.label] - the action, e.g. "Keeping this upload private"
 * @param {string} [p.confirmLabel]
 * @param {{creditsApplied: number, amount: number}} [p.shortfall] - balance is
 *   short: the credits there are are used and `amount` (₹) is paid by Cashfree
 * @returns {Promise<boolean>}
 */
export function confirmCreditChargeModal(p = {}) {
  return new Promise((resolve) => {
    const host = document.createElement("div");
    document.body.appendChild(host);
    const root = createRoot(host);
    let done = false;
    const close = (result) => {
      if (done) return;
      done = true;
      root.unmount();
      host.remove();
      resolve(result);
    };
    root.render(
      <CreditChargeModal
        {...p}
        onCancel={() => close(false)}
        onConfirm={() => close(true)}
      />
    );
  });
}

function CreditChargeModal({
  cost = 0,
  balance = 0,
  freeLimit,
  unit = "item",
  label = "This action",
  confirmLabel,
  shortfall,
  onCancel,
  onConfirm,
}) {
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onCancel();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onCancel]);

  const unitPlural = unit.endsWith("s") ? unit : `${unit}s`;
  const after = shortfall
    ? Math.max(0, balance - shortfall.creditsApplied)
    : Math.max(0, balance - cost);

  return (
    <div
      data-credit-overlay
      className="fixed inset-0 z-[1400] flex items-center justify-center bg-slate-900/50 p-4 backdrop-blur-sm"
      onClick={onCancel}
    >
      <div
        className="w-full max-w-sm overflow-hidden rounded-2xl bg-white shadow-[0_30px_70px_-20px_rgba(21,53,104,0.6)] ring-1 ring-black/5"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center gap-3 border-b border-[#d8e2ee] bg-[#eef3fb] px-5 py-4">
          <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-amber-100 text-amber-600">
            <AlertTriangle className="h-5 w-5" />
          </span>
          <div className="min-w-0">
            <h2 className="text-[15px] font-semibold leading-tight text-[#153568]">
              Free limit reached
            </h2>
            <p className="text-[11px] text-amber-700">This will be charged</p>
          </div>
          <button
            type="button"
            onClick={onCancel}
            className="ml-auto rounded-full p-1 text-slate-400 transition hover:bg-slate-100 hover:text-slate-600"
            aria-label="Cancel"
          >
            <X className="h-4.5 w-4.5" />
          </button>
        </div>

        {/* Body */}
        <div className="space-y-3 px-5 py-4">
          {freeLimit > 0 && (
            <p className="text-[13px] leading-relaxed text-slate-600">
              You&apos;ve used all{" "}
              <span className="font-semibold text-[#153568]">{freeLimit}</span>{" "}
              free {unitPlural}.
            </p>
          )}
          {shortfall ? (
            <p className="text-[13px] leading-relaxed text-slate-600">
              {label} costs{" "}
              <span className="font-semibold text-[#153568]">
                {formatRupees(cost)}
              </span>
              .{" "}
              {shortfall.creditsApplied > 0
                ? `${formatRupees(shortfall.creditsApplied)} comes from your wallet and you pay `
                : "You pay "}
              <span className="font-semibold text-[#153568]">
                {formatRupees(shortfall.amount)}
              </span>{" "}
              by UPI / card.
            </p>
          ) : (
            <p className="text-[13px] leading-relaxed text-slate-600">
              {label} will deduct{" "}
              <span className="font-semibold text-[#153568]">
                {formatRupees(cost)}
              </span>{" "}
              from your wallet.
            </p>
          )}

          <div className="flex items-center justify-between rounded-xl border border-slate-100 bg-slate-50/70 px-3.5 py-2.5">
            <span className="inline-flex items-center gap-1.5 text-[12px] font-medium text-slate-500">
              <Wallet className="h-4 w-4 text-amber-500" /> Wallet
            </span>
            <span className="text-[12.5px] font-semibold text-[#153568]">
              {formatRupees(balance)} <span className="text-slate-400">→</span>{" "}
              {formatRupees(after)}
            </span>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 border-t border-slate-100 px-5 py-3.5">
          <button
            type="button"
            onClick={onCancel}
            className="rounded-lg px-3.5 py-2 text-[13px] font-medium text-slate-600 transition hover:bg-slate-100"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            className="inline-flex items-center gap-1.5 rounded-lg bg-gradient-to-r from-[#153568] to-[#1c4b93] px-4 py-2 text-[13px] font-semibold text-white shadow-sm transition hover:opacity-95"
          >
            {confirmLabel ||
              (shortfall
                ? shortfall.creditsApplied > 0
                  ? `Pay ${formatRupees(shortfall.amount)} (${formatRupees(shortfall.creditsApplied)} from wallet)`
                  : `Pay ${formatRupees(shortfall.amount)}`
                : `Pay ${formatRupees(cost)}`)}
          </button>
        </div>
      </div>
    </div>
  );
}
